import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, BookOpen, MessageCircle, Lock, Crown } from 'lucide-react';

/**
 * Reusable component for Quiz / Discover / Ask sections
 * Used across all REVELIAE apps with consistent styling
 */
const QuizDiscoverChatSection = ({ 
  theme = 'purple',
  appName = '',
  quizPath = '/quiz',
  quizTitle = 'Quiz',
  quizDescription = 'Testez vos connaissances',
  quizFeatures = ['20 questions', 'Explications détaillées', 'Score final'],
  discoverPath = '/knowledge',
  discoverTitle = 'Découvrir',
  discoverDescription = 'Apprenez les fondamentaux',
  discoverFeatures = ['Histoire', 'Symbolisme', 'Conseils pratiques'],
  discoverIsPremium = false,
  chatPath = '/chat',
  chatTitle = 'Poser une Question',
  chatDescription = 'Discutez avec notre expert IA',
  chatExpertName = 'l\'assistant',
  chatFeatures = ['Chat IA', 'Réponses personnalisées', 'Apprentissage guidé']
}) => {
  const navigate = useNavigate();

  // Theme configurations
  const themes = {
    purple: {
      bg: 'bg-white',
      border: 'border-purple-200',
      hoverBorder: 'hover:border-purple-300',
      shadow: 'hover:shadow-purple-100',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      titleColor: 'text-gray-900',
      descColor: 'text-gray-600',
      featureColor: 'text-gray-500',
      featureDot: 'bg-purple-400',
      badge: 'bg-purple-100 text-purple-700 border-purple-200',
      link: 'text-purple-600 group-hover:text-purple-700'
    },
    emerald: {
      bg: 'bg-white',
      border: 'border-emerald-200',
      hoverBorder: 'hover:border-emerald-300',
      shadow: 'hover:shadow-emerald-100',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      titleColor: 'text-gray-900',
      descColor: 'text-gray-600',
      featureColor: 'text-gray-500',
      featureDot: 'bg-emerald-400',
      badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      link: 'text-emerald-600 group-hover:text-emerald-700'
    },
    sky: {
      bg: 'bg-white',
      border: 'border-sky-200',
      hoverBorder: 'hover:border-sky-300',
      shadow: 'hover:shadow-sky-100',
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      titleColor: 'text-gray-900',
      descColor: 'text-gray-600',
      featureColor: 'text-gray-500',
      featureDot: 'bg-sky-400',
      badge: 'bg-sky-100 text-sky-700 border-sky-200',
      link: 'text-sky-600 group-hover:text-sky-700'
    },
    amber: {
      bg: 'bg-white',
      border: 'border-amber-200',
      hoverBorder: 'hover:border-amber-300',
      shadow: 'hover:shadow-amber-100',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      titleColor: 'text-gray-900',
      descColor: 'text-gray-600',
      featureColor: 'text-gray-500',
      featureDot: 'bg-amber-400',
      badge: 'bg-amber-100 text-amber-700 border-amber-200',
      link: 'text-amber-600 group-hover:text-amber-700'
    }
  };

  const t = themes[theme] || themes.purple;

  const modules = [
    {
      id: 'quiz',
      icon: HelpCircle,
      title: quizTitle,
      description: quizDescription,
      features: quizFeatures,
      path: quizPath,
      isPremium: false
    },
    {
      id: 'discover',
      icon: BookOpen,
      title: discoverTitle,
      description: discoverDescription,
      features: discoverFeatures,
      path: discoverPath,
      isPremium: discoverIsPremium
    },
    {
      id: 'chat',
      icon: MessageCircle,
      title: chatTitle,
      description: chatDescription,
      features: chatFeatures,
      path: chatPath,
      isPremium: false
    }
  ];

  return (
    <section className="py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {modules.map((module, index) => {
            const Icon = module.icon;
            
            return (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="group cursor-pointer"
                onClick={() => navigate(module.path)}
                data-testid={`${appName}-${module.id}-module`}
              >
                <div className={`
                  relative p-6 rounded-2xl border transition-all duration-300 h-full
                  ${t.bg} ${t.border} ${t.hoverBorder}
                  hover:shadow-lg ${t.shadow}
                  group-hover:-translate-y-1
                `}>
                  {/* Badge */}
                  <div className={`absolute top-4 right-4 px-2 py-1 text-xs font-medium rounded-full border ${
                    module.isPremium 
                      ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 border-amber-200' 
                      : t.badge
                  }`}>
                    {module.isPremium ? (
                      <span className="flex items-center gap-1">
                        <Crown className="h-3 w-3" />
                        Premium
                      </span>
                    ) : (
                      'Gratuit'
                    )}
                  </div>
                  
                  {/* Icon */}
                  <div className={`w-12 h-12 ${t.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className={`h-6 w-6 ${t.iconColor}`} />
                  </div>
                  
                  {/* Title */}
                  <h3 className={`text-lg font-semibold ${t.titleColor} mb-2`}>
                    {module.title}
                  </h3>
                  
                  {/* Description */}
                  <p className={`text-sm ${t.descColor} mb-4 leading-relaxed`}>
                    {module.description}
                  </p>
                  
                  {/* Features */}
                  <ul className="space-y-2 mb-4">
                    {module.features.map((feature, i) => (
                      <li key={i} className={`flex items-center gap-2 text-sm ${t.featureColor}`}>
                        <span className={`w-1.5 h-1.5 ${t.featureDot} rounded-full`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  {/* Link */}
                  <div className={`flex items-center gap-1 text-sm font-medium ${t.link} transition-colors`}>
                    Explorer
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default QuizDiscoverChatSection;
